import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { gql } from '@apollo/client';
import Button from '../common/Button';
import { TextInput } from '../common/TextInput';
import {Modal} from '../common/Modal';
import { useRequestManager } from '../common/RequestManager';
import { CREATE_RESTOCK_SHIPMENT_PRODUCT_VARIATION } from './RestockShipments.queries';
import { getTranslatedValue } from '../../../common/getTranslatedValue';

const GET_SUPPLIER_PRODUCT_VARIATIONS = gql`
  query GetSupplierProduct($id: String!) {
    supplierProduct(id: $id) {
      id
      name
      variations {
        sku
        supplierProductId
        typeValues {
          id
          value
          type {
            type
          }
        }
      }
    }
  }
`;

interface AddVariationModalProps {
  restockShipmentId: string;
  restockShipmentProductId: string;
  supplierProductId: string;
  onClose: () => void;
  onAdd: () => void;
}

const AddVariationModal: React.FC<AddVariationModalProps> = ({
  restockShipmentId,
  restockShipmentProductId,
  supplierProductId,
  onClose,
  onAdd,
}) => {
  const { trackRequest } = useRequestManager();
  const [selectedVariationSku, setSelectedVariationSku] = useState<string>('');
  const [quantity, setQuantity] = useState('1');
  const [totalCost, setTotalCost] = useState('');
  const [totalCostCurrency, setTotalCostCurrency] = useState('USD');

  const { data, loading } = useQuery(GET_SUPPLIER_PRODUCT_VARIATIONS, {
    variables: { id: supplierProductId },
  });

  const [createVariation] = useMutation(CREATE_RESTOCK_SHIPMENT_PRODUCT_VARIATION);

  const handleAdd = async () => {
    if (!selectedVariationSku) {
      alert('Please select a variation');
      return;
    }

    await trackRequest(
      createVariation({
        variables: {
          input: {
            restockShipmentId: restockShipmentId,
            restockShipmentProductId: restockShipmentProductId,
            supplierProductVariationSku: selectedVariationSku,
            supplierProductVariationSupplierProductId: supplierProductId,
            quantity: parseInt(quantity),
            totalCost: totalCost ? parseFloat(totalCost) : null,
            totalCostCurrency: totalCostCurrency || null,
          },
        },
      }),
      { title: 'Adding variation' }
    );
    onAdd();
  };

  const selectedVariation = data?.supplierProduct?.variations?.find(
    (v: any) => v.sku === selectedVariationSku
  );

  return (
    <Modal onClose={onClose} title="Add Variation">
      <div className="p-6">
        {loading ? (
          <div className="text-center py-4">Loading variations...</div>
        ) : (
          <>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select Variation
              </label>
              <select
                value={selectedVariationSku}
                onChange={(e) => {
                  setSelectedVariationSku(e.target.value);
                  const variation = data?.supplierProduct?.variations?.find(
                    (v: any) => v.sku === e.target.value
                  );
                  if (variation) {
                    setTotalCost((variation.cost * parseInt(quantity || '1')).toString());
                    setTotalCostCurrency(variation.currency);
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">-- Select a variation --</option>
                {data?.supplierProduct?.variations?.map((variation: any) => (
                  <option key={variation.sku} value={variation.sku}>
                    {variation.typeValues?.map((tv: any) => getTranslatedValue(tv.value)).join(' / ') ||
                      variation.sku}{' '}
                    - {variation.cost} {variation.currency} (Stock: {variation.availableStock})
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-4">
              <TextInput
                label="Quantity"
                type="number"
                value={quantity}
                onChange={(e) => {
                  setQuantity(e.target.value);
                  if (selectedVariation) {
                    setTotalCost(
                      (selectedVariation.cost * parseInt(e.target.value || '1')).toString()
                    );
                  }
                }}
                min="1"
              />
            </div>

            <div className="mb-4 flex gap-2">
              <div className="flex-1">
                <TextInput
                  label="Total Cost"
                  type="number"
                  value={totalCost}
                  onChange={(e) => setTotalCost(e.target.value)}
                  placeholder="0.00"
                />
              </div>
              <div className="w-24">
                <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                <select
                  value={totalCostCurrency}
                  onChange={(e) => setTotalCostCurrency(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                  <option value="CNY">CNY</option>
                </select>
              </div>
            </div>

            {selectedVariation && (
              <div className="mb-4 p-3 bg-blue-50 rounded text-sm">
                <p className="font-medium">Selected Variation Info:</p>
                <p>Unit Cost: {selectedVariation.cost} {selectedVariation.currency}</p>
                <p>Available Stock: {selectedVariation.availableStock}</p>
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button preset="white" onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={handleAdd}>Add Variation</Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default AddVariationModal;

